import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
// API
import { useEnv } from '../../../../../context/env-context';
import { useAuth } from '../../../../../hooks/useAuth';
import { makeRequest } from '../../../../../utils/makeRequest';
import { toast } from 'react-toastify';
// components
import AutoSizer from 'react-virtualized-auto-sizer';
import { Spinner } from '../../../../../components/Spinner';
import VirtualizedList from '../../../../../components/VirtualizedList';
import Form from 'react-bootstrap/Form';
// constants
import { REPAYMENT_TRIALS_ACTION, REPAYMENT_TRIALS_TYPE } from '../../../../../constants/fundings';

function NewFundingRepaymentActivityTab({ fundingId, refreshTab, setRefreshTab }) {
  const { apiOriginFunding } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const [spinner, setSpinner] = useState(false);
  const [repaymentTrials, setRepaymentTrials] = useState([]);
  const [checked, setChecked] = useState(true);

  const filteredRepaymentTrials = useMemo(() => {
    let trials = repaymentTrials;

    if (checked) {
      trials = repaymentTrials.filter(x => x.trialActionReason !== 'Not a repayment date');
    }

    return trials.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [checked, repaymentTrials]);

  const getRepaymentTrials = async () => {
    setSpinner(true);
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = { token, url: `${apiOriginFunding}/repayment_trials`, params: { fundingId } };

    try {
      const response = await makeRequest(config);

      setRepaymentTrials(response);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  };

  useEffect(() => {
    getRepaymentTrials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (refreshTab === 'repayment_activity') {
      getRepaymentTrials();
      setRefreshTab('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTab]);

  const tableColumns = useMemo(
    () => [
      {
        key: 'timestamp',
        header: 'Timestamp',
        width: 230,
        renderValue: value => moment.utc(value).local().format('MMM DD, YYYY H:mm:ss (UTCZ)'),
      },
      {
        key: 'trialType',
        header: 'Type',
        width: 80,
        renderValue: value => REPAYMENT_TRIALS_TYPE[value] || value,
      },
      { key: 'jobNumber', header: 'Job #', width: 230 },
      {
        key: 'trialAction',
        header: 'Action',
        width: 230,
        renderValue: value => REPAYMENT_TRIALS_ACTION[value] || value,
      },
      { key: 'trialActionReason', header: 'Reason', width: 230 },
      { key: 'isBlind', header: 'Blind', width: 80, renderValue: value => (value ? 'Yes' : '') },
      { key: 'transferId', header: 'Transfer ID', width: 250 },
    ],
    [],
  );

  const formatCellConditionally = useCallback((_, row) => {
    const REPAYMENT_TRIALS_STATUS_COLOR = {
      FAILED: { color: '#DC3545' },
      CREATED: { color: '#198754' },
      COMPLETED: { color: '#198754' },
    };

    if (REPAYMENT_TRIALS_STATUS_COLOR[row.transferStatus]) {
      return REPAYMENT_TRIALS_STATUS_COLOR[row.transferStatus];
    }

    if (
      row.trialAction === 'NO_REPAYMENT' &&
      row.trialActionReason !== 'Not a repayment date' &&
      row.trialActionReason !== 'Not a business day' &&
      row.trialActionReason !== 'Advance status is CLOSING' &&
      row.trialActionReason !== 'Advance repayment mode is MANUAL'
    ) {
      return { color: '#DC3545' };
    }

    return {};
  }, []);

  return (
    <Spinner visible={spinner} wrapperStyle={{ height: '100%' }}>
      <div className="h-100">
        <div className="p-3 d-flex gap-2 overflow-hidden">
          <div className="d-flex align-items-center">
            <Form.Check checked={checked} onChange={e => setChecked(e.target.checked)} />
            <Form.Label className="bo-common-text-md text-nowrap">
              Hide &lsquo;Not a repayment date&rsquo; trials
            </Form.Label>
          </div>
        </div>
        <AutoSizer>
          {({ height, width }) => (
            <VirtualizedList
              tableRows={filteredRepaymentTrials}
              tableColumns={tableColumns}
              width={width}
              height={height - 56}
              headerTopBorder
              formatCellConditionally={formatCellConditionally}
              rowKey="timestamp"
            />
          )}
        </AutoSizer>
      </div>
    </Spinner>
  );
}

export default NewFundingRepaymentActivityTab;
